import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackTermsPageVisit } from './userJourneyTracker';

const sections = [
  {
    title: 'Acceptance of Terms',
    body: 'By accessing or using Makereels.live ("we", "our", or "us") and any associated services, features, or functionalities (collectively, the "Services"), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service, as well as our Privacy Policy, which is incorporated herein by reference. If you use features that rely on YouTube API Services, you also agree to be bound by the YouTube Terms of Service (https://www.youtube.com/t/terms). If you do not agree to these Terms, you must not access or use the Services. Continued use of the Services constitutes acceptance of any updates or modifications made to these Terms.'
  },
  {
    title: 'License to Use Services',
    body: 'Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for personal or internal business purposes. This license does not grant you any rights to reproduce, distribute, modify, create derivative works from, publicly display, or otherwise exploit any part of the Services except as expressly permitted.'
  },
  {
    title: 'User Content and Responsibility',
    body: 'You retain ownership of any content you upload, submit, or process through the Services; however, by using the Services, you represent and warrant that you have obtained all necessary rights, licenses, permissions, and consents required to use, process, modify, and distribute such content, including but not limited to rights related to copyright, trademark, privacy, publicity, and applicable laws. You acknowledge that Makereels.live does not review, verify, or monitor the legality, ownership, or accuracy of user-submitted content and shall not be responsible for any content uploaded, processed, or distributed through the Services. You are solely responsible for ensuring that your use of the Services does not violate any applicable laws or third-party rights.'
  },
  {
    title: 'YouTube and Social Publishing',
    body: 'If you connect a YouTube or other social media account, you authorize Makereels.live to access and use that account only as needed to publish or schedule content you explicitly request. You are responsible for all content published through your connected accounts and must comply with the terms, policies, and community guidelines of each platform (including YouTube\'s Terms of Service and Community Guidelines). You may disconnect YouTube at any time from the Calendar page in Makereels.live or revoke access from your Google Account permissions settings. See our Privacy Policy for details on how we handle YouTube API data, including the section titled "YouTube API Services".'
  },
  {
    title: 'AI Processing and Outputs',
    body: 'The Services involve automated processing of content, including through AI-based systems, and you acknowledge that outputs generated through such systems may vary in quality, accuracy, completeness, or suitability. Makereels.live makes no representations or warranties regarding the outputs generated through the Services, and you are solely responsible for reviewing and evaluating any outputs prior to use, publication, or distribution. You agree that the use of AI-based processing is inherent to the Services and that such processing may involve transformation, restructuring, or modification of submitted content.'
  },
  {
    title: 'Prohibited Use',
    body: 'You agree not to use the Services in any manner that is unlawful, harmful, abusive, or otherwise inconsistent with the intended use of the platform, including but not limited to uploading content that infringes intellectual property rights, violates privacy or publicity rights, contains malicious code, or is otherwise prohibited by applicable law. You further agree not to attempt to gain unauthorized access to the Services, interfere with system integrity or performance, use automated means to access or use the Services in a manner that exceeds normal usage, or otherwise engage in behavior that could disrupt, damage, or impair the Services.'
  },
  {
    title: 'Fair Use and Service Integrity',
    body: 'While certain plans may be described as offering unlimited usage, such usage is subject to fair, reasonable, and non-abusive use. We reserve the right, at our sole discretion, to monitor usage patterns and take any action deemed necessary to protect the integrity, performance, or availability of the Services, including but not limited to limiting usage, throttling performance, restricting access, or suspending accounts, without prior notice, where usage is determined to be excessive, automated beyond normal use, or otherwise inconsistent with intended use.'
  },
  {
    title: 'Payments and Subscriptions',
    body: 'Certain features of the Services may require payment. By subscribing to a paid plan, you agree to provide accurate payment information and authorize the use of third-party payment processors. All fees are non-refundable except where required by applicable law. We reserve the right to modify pricing, introduce new charges, or change billing structures at any time, with such changes becoming effective upon posting or notification as applicable.'
  },
  {
    title: 'Subscription & Cancellation',
    body: 'Users may cancel their subscription at any time through the following methods. Contact Support: Users can request cancellation by contacting us via email or through the in-app support chat. Payment Provider Cancellation: If the subscription was initiated through a third-party payment provider (such as Google Pay or similar services), users may also cancel the recurring mandate directly from their respective payment platform. Please note that cancellation will stop future billing cycles. Any charges already processed are non-refundable unless otherwise stated. For assistance, please contact our support team.'
  },
  {
    title: 'Intellectual Property',
    body: 'All rights, title, and interest in and to the Services, including but not limited to software, design, functionality, and underlying technology, remain the exclusive property of Makereels.live or its licensors. Nothing in these Terms grants you any ownership or rights to the Services beyond the limited license described herein.'
  },
  {
    title: 'Disclaimer of Warranties',
    body: 'The Services are provided on an "as is" and "as available" basis without warranties of any kind, whether express or implied, including but not limited to warranties of accuracy, reliability, availability, merchantability, fitness for a particular purpose, or non-infringement. We do not guarantee that the Services will be uninterrupted, error-free, or free of harmful components.'
  },
  {
    title: 'Limitation of Liability',
    body: 'To the maximum extent permitted by law, Makereels.live shall not be liable for any indirect, incidental, consequential, special, or exemplary damages, including but not limited to loss of data, revenue, profits, or business opportunities, arising out of or in connection with your use of the Services, even if advised of the possibility of such damages.'
  },
  {
    title: 'Indemnification',
    body: 'You agree to defend, indemnify, and hold harmless Makereels.live, its affiliates, officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, or expenses, including reasonable legal fees, arising out of or in any way connected with your use of the Services, your content, or your violation of these Terms or any applicable laws or third-party rights.'
  },
  {
    title: 'Termination',
    body: 'We reserve the right, at our sole discretion, to suspend, restrict, or terminate your access to the Services at any time, without prior notice or liability, for any reason, including but not limited to violations of these Terms or suspected misuse of the Services.'
  },
  {
    title: 'Governing Law',
    body: 'These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising out of or relating to these Terms shall be subject to the exclusive jurisdiction of the courts located in India.'
  },
  {
    title: 'Changes to Terms',
    body: 'We may modify or update these Terms at any time. Continued use of the Services after such changes constitutes your acceptance of the revised Terms.'
  }
];

function TermsPage() {
  useEffect(() => {
    trackTermsPageVisit();
  }, []);

  const paragraphStyle = {
    margin: 0,
    lineHeight: 1.7,
    color: '#334155',
    textAlign: 'justify',
    fontFamily: "'Inter', sans-serif"
  };

  const linkStyle = { color: '#5170ff', textDecoration: 'none' };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Inter', sans-serif", display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, padding: '48px 16px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', background: '#ffffff', borderRadius: '16px', padding: '40px 28px', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)' }}>
          <h1 style={{ margin: 0, fontSize: '2.75rem', color: '#0f172a', textAlign: 'center', fontFamily: "'Montserrat', sans-serif" }}>Terms and Conditions</h1>
          <p style={{ marginTop: '12px', marginBottom: '24px', color: '#475569' }}>
            Effective Date: 19/03/2025
          </p>

          {sections.map((section) => (
            <section key={section.title} style={{ marginBottom: '28px' }}>
              <h2 style={{ marginBottom: '10px', fontSize: '1.2rem', color: '#1e293b', fontFamily: "'Montserrat', sans-serif" }}>{section.title}</h2>
              <p style={paragraphStyle}>
                {section.title === 'Acceptance of Terms' ? (
                  <>
                    By accessing or using Makereels.live (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) and any
                    associated services, features, or functionalities (collectively, the &quot;Services&quot;), you
                    acknowledge that you have read, understood, and agree to be bound by these Terms of Service, as
                    well as our Privacy Policy, which is incorporated herein by reference. If you use features that
                    rely on YouTube API Services, you also agree to be bound by the{' '}
                    <a
                      href="https://www.youtube.com/t/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={linkStyle}
                    >
                      YouTube Terms of Service
                    </a>
                    . If you do not agree to these Terms, you must not access or use the Services. Continued use of
                    the Services constitutes acceptance of any updates or modifications made to these Terms.
                  </>
                ) : section.title === 'YouTube and Social Publishing' ? (
                  <>
                    If you connect a YouTube or other social media account, you authorize Makereels.live to access and
                    use that account only as needed to publish or schedule content you explicitly request. You are
                    responsible for all content published through your connected accounts and must comply with the terms,
                    policies, and community guidelines of each platform (including the{' '}
                    <a
                      href="https://www.youtube.com/t/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={linkStyle}
                    >
                      YouTube Terms of Service
                    </a>{' '}
                    and Community Guidelines). By using YouTube features in Makereels.live, you agree to be bound by
                    the YouTube Terms of Service. You may disconnect YouTube at any time from the{' '}
                    <Link to="/calender" style={linkStyle}>Calendar</Link> page in Makereels.live or revoke access
                    from your Google Account permissions settings. See our{' '}
                    <Link to="/policy#youtube-api-services" style={linkStyle}>Privacy Policy</Link> for details on how
                    we handle YouTube API data, including the section titled &quot;YouTube API Services&quot;.
                  </>
                ) : (
                  section.body
                )}
              </p>
            </section>
          ))}

          <section>
            <h2 style={{ marginBottom: '10px', fontSize: '1.2rem', color: '#1e293b', fontFamily: "'Montserrat', sans-serif" }}>Contact</h2>
            <p style={paragraphStyle}>
              If you have any questions regarding these Terms, you may contact us at{' '}
              <a href="mailto:Aksharshare@gmail.com" style={linkStyle}>Aksharshare@gmail.com</a>
              {' '}or via chat support.
            </p>
          </section>
        </div>
      </div>
      <footer className="footer">
        <div className="footer-content">
          <p className="footer-text">© 2026 Makereels.live. All rights reserved.</p>
          <nav className="footer-nav" aria-label="Legal">
            <Link to="/policy" className="footer-link">Privacy Policy</Link>
            <span className="footer-sep" aria-hidden="true">·</span>
            <Link to="/terms" className="footer-link">Terms of Service</Link>
            <span className="footer-sep" aria-hidden="true">·</span>
            <Link to="/refund" className="footer-link">Refund Policy</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

export default TermsPage;
